import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, retry, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { InitializationService } from '../services/initialization.service';
import { SignOutService } from '../services/sign-out.service';
import { ToastService } from '../services/toast.service';
import { RefreshTokenService } from '../services/refresh-token.service';
import { AppModalService } from '../services/app-modal.service';
import { ActivityTokenService } from 'src/app/activity/services/activity-token.service';
import { USE_ACTIVITY_TOKEN } from 'src/app/activity/constants/activity-http-context';

const maxRetries = 1;
const delayMs = 1500;

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private initializationService = inject(InitializationService);
  private signOutService = inject(SignOutService);
  private toastService = inject(ToastService);
  private refreshTokenService = inject(RefreshTokenService);
  private appModalService = inject(AppModalService);
  private activityTokenService = inject(ActivityTokenService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler) {
    if (request.context.get(USE_ACTIVITY_TOKEN)) {
      return this.handleActivityRequest(request, next);
    }
    return this.handleRequest(request, next);
  }

  handleRequest(request: HttpRequest<unknown>, next: HttpHandler) {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        switch (err.status) {
          case 0: {
            if (!this.skipRetryApi(request.url)) {
              return next.handle(request).pipe(
                retry({ count: maxRetries, delay: this.delayNotifier }),
                catchError(err => {
                  this.appModalService.showDisconnectedDialog();
                  return throwError(() => err);
                })
              );
            }
            break;
          }

          case 401 /** Unauthorized */: {
            if (request.url.includes('www.zipcodeapi.com')) {
              break;
            }

            localStorage.removeItem('token');
            const refresh = localStorage.getItem('refresh');
            if (!refresh) {
              if (!request.url.endsWith('/authentication')) {
                this.signOutService.signOut();
              } else {
                // we are handling the error for this in the api call itself
              }
              break;
            }

            if (request.url.endsWith('/refresh')) {
              // Refresh api returned 401
              this.signOutService.signOut();
              break;
            }

            // Check if we are already calling the refresh token api
            if (!this.refreshTokenService.callingRefresh) {
              return this.refreshTokenService.refreshToken(refresh!).pipe(
                switchMap(() => {
                  // Retry the original request with the new token
                  const authReq = request.clone({
                    setHeaders: {
                      Authorization: `Bearer ${this.initializationService.getReceivedJwt()}`,
                    },
                  });

                  return next.handle(authReq);
                })
              );
            } else {
              // When calling refresh token we do not want to call it again,
              // Instead we want to wait for the refresh token api to update the RefreshTokenCallObservable
              // and then call the other requests with the new token
              // We need to return next.handle() to the interceptor, that's why we are doing things in the switchMap instead of the subscribe
              return new Observable(observer => {
                const subscription = this.refreshTokenService
                  .getRefreshTokenCallObservable()
                  .subscribe(() => {
                    observer.next(null);
                    observer.complete();
                    subscription.unsubscribe();
                  });
              }).pipe(
                switchMap(() => {
                  // This block will wait till the refresh token subject emit a value
                  const authReq = request.clone({
                    setHeaders: {
                      Authorization: `Bearer ${this.initializationService.getReceivedJwt()}`,
                    },
                  });

                  return next.handle(authReq);
                })
              );
            }
          }

          case 403 /** Forbidden */:
            this.initializationService.navigateToGenericPage(true);
            break;

          case 504 /** Server Timeout */:
            break;

          case 500 /** Internal Server Error */:
            if (request.url.includes('/authentication/refresh')) {
              // if server is failing on refreshing the token, there is nothing
              // we can do except signing out the user.
              this.signOutService.signOut();
            }
            this.toastService.showError($localize`Something went wrong from our end`);
            break;
        }

        return throwError(() => err);
      })
    );
  }

  handleActivityRequest(request: HttpRequest<unknown>, next: HttpHandler) {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 401) {
          return throwError(() => err);
        }

        if (this.activityTokenService.hasToken()) {
          return this.retryWithActivityToken(
            request,
            next,
            this.activityTokenService.getActivityToken()
          );
        }

        return this.refreshMainToken().pipe(
          switchMap(token => this.retryWithToken(request, next, token)),
          catchError(retryErr => {
            // only a failed refresh or a 401 with the refreshed token falls back
            if (retryErr instanceof HttpErrorResponse && retryErr.status !== 401) {
              return throwError(() => retryErr);
            }
            return this.retryWithActivityToken(
              request,
              next,
              this.activityTokenService.switchToActivityToken()
            );
          })
        );
      })
    );
  }

  retryWithActivityToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    activityToken$: Observable<string>
  ) {
    return activityToken$.pipe(
      catchError(tokenErr => {
        this.activityTokenService.expireSession();
        return throwError(() => tokenErr);
      }),
      switchMap(token => this.retryWithToken(request, next, token))
    );
  }

  retryWithToken(request: HttpRequest<unknown>, next: HttpHandler, token: string) {
    return next.handle(request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
  }

  /** Refresh the main session without navigating to sign in, and return the new token */
  refreshMainToken(): Observable<string> {
    const refresh = localStorage.getItem('refresh');
    if (!refresh) {
      return throwError(() => new Error('No refresh token'));
    }

    // when a refresh is already running, wait for it instead of calling it again
    const refresh$ = this.refreshTokenService.callingRefresh
      ? this.refreshTokenService.getRefreshTokenCallObservable().pipe(take(1))
      : this.refreshTokenService.refreshToken(refresh, false);

    return refresh$.pipe(map(() => this.initializationService.getReceivedJwt()));
  }

  skipRetryApi(url: string) {
    return (
      url.includes('/api/patients/memberfilter') && !url.includes('tree') && !url.includes('export')
    );
  }

  delayNotifier() {
    return timer(delayMs);
  }
}
